/**
 * Nach der Anmeldung: Spielstand auf diesem Gerät und Spielstand im Konto zusammenführen.
 *
 * Früher wurde der Cloud-Stand still über den lokalen geladen. Wer sich gerade bei der Parteigründung
 * anmeldete, landete so ohne Hinweis mitten in einer alten Partie. Jetzt gilt:
 * - Abgeschlossene oder Mehrspieler-Stände in der Cloud werden ignoriert.
 * - Gleiche Partie (gleicher Seed): der weiter fortgeschrittene Stand gewinnt.
 * - Auf dem Gerät läuft noch keine Partie (Parteigründung): Cloud-Stand laden und mit Hinweis anzeigen,
 *   der zurück zur Parteigründung führt.
 * - Beide Stände laufen: Spieler entscheidet.
 */
import { create } from 'zustand';
import { S, type GameState } from '../../engine/state';
import { journal } from '../../engine/actions';
import { useGame, type Saved } from '../../store/game';
import { track } from '../../lib/telemetry';

type Pending = { kind: 'loaded'; prev: Saved; cloud: Saved } | { kind: 'conflict'; cloud: Saved };
export const useSaveChoice = create<{ p: Pending | null }>(() => ({ p: null }));

const clone = <T,>(x: T): T => JSON.parse(JSON.stringify(x));
const running = (s: GameState | null | undefined) => !!s && !!s.phase && s.phase !== 'program' && s.phase !== 'end';

/** Entscheidet nach dem Laden des Cloud-Stands, was passiert. */
export function mergeCloud(cloud: Saved | null) {
  if (!cloud || !running(cloud.state) || cloud.state.mp) return; // nichts Brauchbares im Konto
  const local = S;
  const { loadState } = useGame.getState();
  if (local && local.seed === cloud.state.seed) {
    if (cloud.journal.length > journal.length) loadState(cloud.state, cloud.journal);
    return;
  }
  if (!running(local)) {
    const prev = { state: clone(local), journal: journal.slice() };
    loadState(cloud.state, cloud.journal);
    useSaveChoice.setState({ p: { kind: 'loaded', prev, cloud } });
    track('Save.cloudLoaded', { phase: cloud.state.phase });
    return;
  }
  useSaveChoice.setState({ p: { kind: 'conflict', cloud } });
}

const label = (s: GameState) => {
  const ph: Record<string, string> = { campaign: 'Wahlkampf', night: 'Wahlabend', talks: 'Koalitionsverhandlung', govern: 'Regierung', review: 'Bilanz der Legislatur' };
  return `„${s.name}“, ${s.leg + 1}. Legislatur, ${ph[s.phase] ?? s.phase}`;
};

/** Hinweis bzw. Auswahl über dem Spiel. */
export function SaveChoice() {
  const p = useSaveChoice((st) => st.p);
  const { loadState, newGame } = useGame();
  if (!p) return null;
  const close = () => useSaveChoice.setState({ p: null });
  if (p.kind === 'loaded') return (
    <div className="card notice" role="status">
      <b>Dein gespeichertes Spiel aus deinem Konto wurde geladen:</b> {label(p.cloud.state)}.{' '}
      <span className="actions" style={{ display: 'inline-flex', gap: '.5rem', marginTop: '.5rem' }}>
        <button type="button" className="small" onClick={close}>Weiterspielen</button>
        <button type="button" className="small" onClick={() => {
          if (p.prev.state?.phase === 'program') loadState(p.prev.state, p.prev.journal); else newGame();
          track('Save.cloudDiscarded', {}); close();
        }}>Stattdessen neue Partei gründen</button>
      </span>
    </div>
  );
  return (
    <div className="card notice" role="alertdialog" aria-label="Spielstand wählen">
      <b>In deinem Konto liegt ein anderer Spielstand.</b>
      <p className="sub">Auf diesem Gerät: {label(S)}<br />Im Konto: {label(p.cloud.state)}</p>
      <p className="sub">Der Stand, den du nicht wählst, wird überschrieben.</p>
      <span className="actions" style={{ display: 'inline-flex', gap: '.5rem' }}>
        <button type="button" className="small" onClick={() => { loadState(S, journal.slice()); track('Save.conflict', { kept: 'local' }); close(); }}>Diesen Stand behalten</button>
        <button type="button" className="small" onClick={() => { loadState(p.cloud.state, p.cloud.journal); track('Save.conflict', { kept: 'cloud' }); close(); }}>Stand aus dem Konto laden</button>
      </span>
    </div>
  );
}
